import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../ui/Button';
import { Dialog, ReasonDialog } from './AccountBits';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { useAuth } from '../../../context/AuthContext';
import { useOrgTimezone, formatDateTimeInTz } from '../../../utils/timezone';
import { errorMessage } from '../../../utils/apiResponse';
import { money, num } from '../../../utils/nurseryMoney';
import { cn } from '../../../utils/cn';
import {
  CREDIT_REASON_LABELS,
  type CreditNoteRequest, type CreditRequestAccess, type CreditRequestStatus,
} from '../../../types/nurseryAccounts';

/**
 * Credit notes a nursery manager asked for. An org admin or an Accounts Admin
 * approves (which raises the credit note) or declines with a reason; the
 * person who asked can withdraw while it waits. Nobody approves their own.
 * The server decides all of it; the buttons only follow `access`.
 */

const STATUS_STYLE: Record<CreditRequestStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-700',
  withdrawn: 'bg-neutral-100 text-neutral-600',
};

const STATUS_LABEL: Record<CreditRequestStatus, string> = {
  pending: 'Waiting for approval',
  approved: 'Approved',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

export interface CreditRequestListProps {
  requests: CreditNoteRequest[];
  access: CreditRequestAccess | null;
  /** Called after any decision, so the page reloads. */
  onChanged: () => void;
  /** Where the invoice number links to; none on the invoice's own page. */
  invoiceHref?: (r: CreditNoteRequest) => string | null;
  /** The request to ring, from a notification. */
  highlightId?: number | null;
}

export function CreditRequestList({ requests, access, onChanged, invoiceHref, highlightId }: CreditRequestListProps) {
  const { user } = useAuth();
  const tz = useOrgTimezone();
  const [approving, setApproving] = useState<CreditNoteRequest | null>(null);
  const [declining, setDeclining] = useState<CreditNoteRequest | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const withdraw = async (r: CreditNoteRequest) => {
    setBusy(r.id);
    setError(null);
    try {
      const res = await nurseryAccountsService.withdrawCreditRequest(r.id);
      if (res.success) onChanged();
      else setError(errorMessage(res, 'Could not withdraw the request'));
    } catch (e) {
      setError(errorMessage(e, 'Could not withdraw the request'));
    }
    setBusy(null);
  };

  return (
    <div className="space-y-3">
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      {requests.map((r) => {
        const own = user?.id != null && r.requested_by_user_id === Number(user.id);
        const href = invoiceHref?.(r) ?? null;
        return (
          <div key={r.id} id={`credit-request-${r.id}`}
            className={cn('bg-white border rounded-xl p-4 space-y-3', highlightId === r.id ? 'border-primary-400 ring-2 ring-primary-200' : 'border-neutral-200')}>
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
              <div className="min-w-0">
                <p className="font-semibold text-neutral-900 break-words">
                  {money(r.total_amount)} inc GST on{' '}
                  {href ? <Link to={href} className="text-primary-700 hover:underline">{r.invoice_number}</Link> : r.invoice_number}
                </p>
                <p className="text-sm text-neutral-600 break-words">
                  {[r.customer_name, r.order_number && `order ${r.order_number}`, CREDIT_REASON_LABELS[r.reason]].filter(Boolean).join(' · ')}
                </p>
              </div>
              <span className={cn('text-xs font-medium rounded-full px-2 py-0.5 whitespace-nowrap', STATUS_STYLE[r.status])}>
                {STATUS_LABEL[r.status]}
              </span>
            </div>

            <blockquote className="border-l-4 border-neutral-200 pl-3 text-sm text-neutral-800 break-words whitespace-pre-line">
              {r.explanation}
            </blockquote>
            <p className="text-xs text-neutral-500 break-words">
              Asked by {r.requested_by_name || 'someone no longer here'}, {formatDateTimeInTz(r.requested_at, tz)}
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                    <th className="py-1.5 pr-3 font-medium">What is credited</th>
                    <th className="py-1.5 px-3 font-medium text-right">Ex GST</th>
                    <th className="py-1.5 pl-3 font-medium text-right">GST</th>
                  </tr>
                </thead>
                <tbody>
                  {r.lines.map((l, i) => (
                    <tr key={i} className="border-b border-neutral-100 last:border-0 align-top">
                      <td className="py-1.5 pr-3 min-w-[10rem] break-words">{l.description}</td>
                      <td className="py-1.5 px-3 text-right">{money(l.line_total)}</td>
                      <td className="py-1.5 pl-3 text-right">{money(l.gst_amount)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-1.5 pr-3">Total</td>
                    <td className="py-1.5 px-3 text-right">{money(r.subtotal)}</td>
                    <td className="py-1.5 pl-3 text-right">{money(num(r.total_amount) - num(r.subtotal))}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {r.status !== 'pending' && (
              <p className="text-sm text-neutral-700 break-words">
                {r.status === 'withdrawn'
                  ? 'Withdrawn by the person who asked.'
                  : `${STATUS_LABEL[r.status]} by ${r.decided_by_name || 'an approver'}${r.decided_at ? `, ${formatDateTimeInTz(r.decided_at, tz)}` : ''}.`}
                {r.credit_note_number && <> Credit note <span className="font-medium">{r.credit_note_number}</span>.</>}
                {r.decision_note && <> “{r.decision_note}”</>}
              </p>
            )}

            {r.status === 'pending' && (
              <div className="flex flex-wrap items-center gap-2">
                {access?.can_approve && !own && (
                  <>
                    <Button size="sm" onClick={() => setApproving(r)}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => setDeclining(r)}>Decline</Button>
                  </>
                )}
                {own && (
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => withdraw(r)}>
                    {busy === r.id ? 'Working…' : 'Withdraw'}
                  </Button>
                )}
                {access?.can_approve && own && (
                  <p className="text-xs text-neutral-500">Someone else has to approve a credit note you asked for.</p>
                )}
                {!access?.can_approve && !own && (
                  <p className="text-xs text-neutral-500">An organisation admin or an Accounts Admin approves this.</p>
                )}
              </div>
            )}
          </div>
        );
      })}

      {approving && (
        <ApproveDialog request={approving} onClose={() => setApproving(null)}
          onDone={() => { setApproving(null); onChanged(); }} />
      )}
      {declining && (
        <ReasonDialog
          title={`Decline the credit on ${declining.invoice_number}`}
          body={`Nothing is credited. ${declining.requested_by_name || 'The person who asked'} is told, with your reason.`}
          label="Why it is declined"
          confirmLabel="Decline"
          danger
          onClose={() => setDeclining(null)}
          onConfirm={async (note) => {
            let res;
            try {
              res = await nurseryAccountsService.declineCreditRequest(declining.id, note);
            } catch (e) {
              throw new Error(errorMessage(e, 'Could not decline it'));
            }
            if (!res.success) throw new Error(errorMessage(res, 'Could not decline it'));
            setDeclining(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function ApproveDialog({ request, onClose, onDone }: { request: CreditNoteRequest; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.approveCreditRequest(request.id, note.trim());
      if (res.success) {
        onDone();
        return;
      }
      setError(errorMessage(res, 'Could not approve it'));
    } catch (e) {
      setError(errorMessage(e, 'Could not approve it'));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={`Approve ${money(request.total_amount)} on ${request.invoice_number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={approve} disabled={busy}>{busy ? 'Working…' : 'Approve and raise the credit note'}</Button>
        </>
      }
    >
      <p className="text-sm text-neutral-700 break-words">
        This raises the credit note now, and it comes off what {request.customer_name || 'the customer'} owes on{' '}
        {request.invoice_number}. A credit note can be voided later, but not changed.
      </p>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      <div>
        <label htmlFor="approve-note" className="block text-sm font-medium text-neutral-700 mb-1">Note to the person who asked (optional)</label>
        <textarea id="approve-note" rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
      </div>
    </Dialog>
  );
}

export default CreditRequestList;
